import { useEffect, useRef, useState } from 'react';

const AlphaRail = ({ show, letters, containerRef, onJump, entries, variant = 'mobile' }) => {
  const railRef = useRef(null);
  const [railPosition, setRailPosition] = useState(null);
  const [activeLetter, setActiveLetter] = useState(null);

  useEffect(() => {
    if (!show || variant === 'desktop') return;

    const updateRailPosition = () => {
      const railEl = railRef.current;
      const containerEl = containerRef.current;
      if (!railEl || !containerEl) return;

      const items = containerEl.querySelectorAll('.list-group-item');
      if (!items || items.length === 0) return;

      const firstRect = items[0].getBoundingClientRect();
      const lastRect = items[items.length - 1].getBoundingClientRect();
      const railHeight = railEl.offsetHeight || 0;

      const minCenter = firstRect.top + railHeight / 2;
      const maxCenter = lastRect.bottom - railHeight / 2;
      const viewportCenter = window.innerHeight / 2;

      if (maxCenter < minCenter) {
        setRailPosition({
          top: Math.max(firstRect.top, 8),
          transform: 'none',
        });
        return;
      }

      const clampedCenter = Math.min(Math.max(viewportCenter, minCenter), maxCenter);
      const top = clampedCenter - railHeight / 2;
      setRailPosition({
        top,
        transform: 'none',
      });
    };

    updateRailPosition();
    const opts = { passive: true };
    window.addEventListener('scroll', updateRailPosition, opts);
    window.addEventListener('resize', updateRailPosition);
    return () => {
      window.removeEventListener('scroll', updateRailPosition, opts);
      window.removeEventListener('resize', updateRailPosition);
    };
  }, [show, entries, containerRef, variant]);

  useEffect(() => {
    if (!show || !letters || letters.length === 0) return;

    const updateActive = () => {
      const scrollContainer = variant === 'desktop' ? containerRef.current : null;
      const nodes = Array.from((scrollContainer || document).querySelectorAll('[data-letter]'));

      if (!nodes.length) return;

      if (variant === 'desktop' && scrollContainer) {
        // Desktop: track position relative to scroll container
        const scrollTop = scrollContainer.scrollTop;

        let current = nodes[0];
        for (const node of nodes) {
          const nodeTop = node.offsetTop;
          if (nodeTop <= scrollTop + 10) {
            current = node;
          } else {
            break;
          }
        }

        const letter = current.getAttribute('data-letter');
        if (letter) setActiveLetter(letter);
      } else {
        // Mobile: track position relative to viewport
        const targetLine = 20;
        const firstRect = nodes[0].getBoundingClientRect();
        const lastRect = nodes[nodes.length - 1].getBoundingClientRect();

        if (firstRect.top > targetLine || lastRect.bottom < targetLine) {
          setActiveLetter(null);
          return;
        }

        let current = nodes[0];
        for (const node of nodes) {
          const rect = node.getBoundingClientRect();
          if (rect.top <= targetLine) {
            current = node;
          } else {
            break;
          }
        }

        const letter = current.getAttribute('data-letter');
        if (letter) setActiveLetter(letter);
      }
    };

    updateActive();

    const opts = { passive: true };

    if (variant === 'desktop' && containerRef.current) {
      // Desktop: listen to container scroll
      const scrollContainer = containerRef.current;
      scrollContainer.addEventListener('scroll', updateActive, opts);
      return () => {
        scrollContainer.removeEventListener('scroll', updateActive, opts);
      };
    } else {
      // Mobile: listen to window scroll
      const handleScroll = () => {
        updateActive();
      };

      const handleResize = () => {
        updateActive();
      };

      window.addEventListener('scroll', handleScroll, opts);
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('scroll', handleScroll, opts);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [show, letters, variant, containerRef]);

  if (!show || !letters || letters.length === 0) return null;

  const className =
    variant === 'desktop' ? 'alpha-rail alpha-rail--desktop' : 'alpha-rail d-md-none';
  const style =
    variant !== 'desktop' && railPosition
      ? { top: `${railPosition.top}px`, transform: railPosition.transform }
      : undefined;

  return (
    <div className={className} aria-label='Alphabet jump list' ref={railRef} style={style}>
      {letters.map((letter) => (
        <button
          key={letter}
          type='button'
          className='alpha-rail__item'
          data-active={activeLetter === letter ? 'true' : 'false'}
          onClick={() => {
            if (onJump) onJump(letter);
          }}
          aria-label={`Jump to words starting with ${letter}`}
        >
          {letter}
        </button>
      ))}
    </div>
  );
};

export default AlphaRail;
